/**
 * 3D 层总入口：创建 React Three Fiber Canvas，装配模型、雪花、滚动相机和后处理。
 * 这里主要负责“系统配置”，具体动画分别放在子组件中。
 */
import { useState } from 'react';
import { Canvas } from '@react-three/fiber';
import {
    // OrbitControls,
    PerformanceMonitor,
    ScrollControls,
    Stats,
    Preload,
} from '@react-three/drei';
import round from 'lodash.round';
import { TierResult } from 'detect-gpu';
import Model from './ModelNew';
import Particles from './Particles';
import { data } from '../../data';
import Postprocessing from './Postprocessing';
import { useSoundConfigState } from '../../atoms/sound-config';
// import Rig from './Rig';
import FlyingCamera from './FlyingCamera';
import { useGpuState } from '../../atoms/gpu';
import { useScrollControlsState } from '../../atoms/scroll-controls';
import { WebGLRenderer } from 'three';

/** 只有桌面高性能 GPU 且估算帧率足够时才启用景深和 SMAA。 */
const shouldUsePostprocessingBasedOnGPUState = ({
    isMobile,
    fps,
    tier,
    gpu,
}: TierResult) => {
    if (
        isMobile ||
        tier < 3 ||
        gpu === 'intel hd graphics 620' ||
        (typeof fps === 'number' ? fps < 50 : true)
    ) {
        return false;
    }

    return true;
};

// data.ts 的章节数直接决定 ScrollControls 的虚拟页面数。
const totalScreens = Object.keys(data).length;

const WebGL = () => {
    const [gpuState] = useGpuState();
    // DPR 越高越清晰但越耗 GPU；首帧先限制到 1.25，后续由性能监视器调整。
    const [dpr, setDpr] = useState(Math.min(devicePixelRatio, 1.25));
    const [usePostprocessing] = useState(
        gpuState ? shouldUsePostprocessingBasedOnGPUState(gpuState) : false,
    );
    const [soundConfig] = useSoundConfigState();
    const [scrollEnabled] = useScrollControlsState();
    // 用户选择声音后才算体验正式开始，此前 Canvas 只按需渲染静态帧。
    const appReady = soundConfig.picked;

    return (
        <div className="canvas-wrapper">
            <Canvas
                // 相机初始值与 data[1].from 一致，保证第一帧没有跳动。
                camera={{
                    position: [193, 78, 20],
                    fov: 32.27,
                    near: 10,
                    far: 1000,
                }}
                dpr={dpr}
                // 手动创建 renderer：使用后处理时关闭原生 MSAA，避免重复抗锯齿成本。
                gl={(canvas) =>
                    new WebGLRenderer({
                        canvas,
                        antialias:
                            !usePostprocessing &&
                            (gpuState && typeof gpuState.fps === 'number'
                                ? gpuState.fps > 40
                                : false),
                    })
                }
                // flat 关闭 R3F 的默认色调映射；模型贴图按原设计直接显示。
                flat
                // 启动遮罩期间按需渲染，进入体验后才持续执行 useFrame。
                frameloop={appReady ? 'always' : 'demand'}
            >
                <color attach="background" args={['black']} />

                {/* {appReady && <Rig to={toVec} lookAt={cameraLookAtVec} />} */}
                {/* 一套 500 点的循环雪花粒子，以及完整 GLB 场景。 */}
                <Particles count={500} position={[0, 100, 0]} />
                <Model />
                {/* 提前请求场景中声明的资源，供 Preloader 汇总进度。 */}
                <Preload all />

                <ScrollControls
                    // distance=3 拉长实际滚动距离，damping=4 让滚动变化更平滑。
                    pages={totalScreens}
                    // infinite
                    distance={3}
                    damping={4}
                    enabled={appReady && scrollEnabled}
                >
                    <FlyingCamera />
                </ScrollControls>
                {/* <OrbitControls /> */}

                <PerformanceMonitor
                    // 根据运行时性能动态在约 0.75～2 DPR 之间取值。
                    onChange={({ factor }) =>
                        setDpr(
                            Math.min(
                                Math.min(devicePixelRatio, 2),
                                round(0.75 + 1.25 * factor, 1),
                            ),
                        )
                    }
                >
                    <Postprocessing enabled={usePostprocessing} />
                </PerformanceMonitor>
                {/* Stats 只在 pnpm dev 中显示，生产包不会出现性能面板。 */}
                {import.meta.env.DEV && <Stats />}
            </Canvas>

            {/* CSS 叠加的静态光晕，不占用额外 WebGL 绘制调用。 */}
            <img
                src="/light-opt.png"
                alt=""
                className="light"
                decoding="async"
                loading="lazy"
            />
        </div>
    );
};

export default WebGL;
